import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  MessageSquare, 
  Heart, 
  Star, 
  MapPin, 
  Clock, 
  Search, 
  Filter,
  ChevronRight,
  ArrowRight,
  Plus,
  Globe,
  Award,
  TrendingUp,
  BookOpen,
  Camera,
  ThumbsUp,
  Share2,
  Flag,
  UserPlus,
  Calendar,
  Tag,
  Eye,
  MessageCircle,
  Send,
  Smile,
  Image,
  Video,
  FileText,
  Link,
  MoreHorizontal,
  CheckCircle,
  AlertCircle,
  Info,
  Zap,
  Target,
  Home,
  GraduationCap,
  Briefcase,
  CreditCard,
  Shield,
  Lightbulb,
  HelpCircle,
  PartyPopper
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { AnimatedCounter } from '../components/ui';

interface Story {
  id: string;
  title: string;
  content: string;
  author: {
    name: string;
    avatar: string;
    location: string;
    joinDate: string;
    verified: boolean;
  };
  category: string;
  tags: string[];
  likes: number;
  comments: number;
  shares: number;
  views: number;
  createdAt: string;
  updatedAt: string;
  isLiked: boolean;
  isBookmarked: boolean;
  images?: string[];
  video?: string;
  type: 'success' | 'advice' | 'question' | 'resource' | 'celebration';
  helpful: number;
  difficulty: 'easy' | 'medium' | 'hard';
  timeToRead: number;
}

interface Mentor {
  id: string;
  name: string;
  title: string;
  company: string;
  avatar: string;
  location: string;
  expertise: string[];
  experience: number;
  rating: number;
  reviews: number;
  languages: string[];
  availability: 'available' | 'busy' | 'away';
  responseTime: string;
  verified: boolean;
  bio: string;
  specializations: string[];
  achievements: string[];
  isOnline: boolean;
  lastActive: string;
}

interface CommunityStats {
  totalMembers: number;
  activeToday: number;
  storiesShared: number;
  questionsAnswered: number;
  mentorsAvailable: number;
  countriesRepresented: number;
  languagesSupported: number;
}

const ConnectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stories, setStories] = useState<Story[]>([]);
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [stats, setStats] = useState<CommunityStats | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'stories' | 'mentors'>('stories');
  const [newPostContent, setNewPostContent] = useState('');
  const [showNewPost, setShowNewPost] = useState(false);

  useSEO({
    title: 'Connections & Stories - MyNewStart',
    description: 'Community space for peer stories, mentor Q&A, and mutual support.',
    keywords: ['community', 'stories', 'mentors', 'connections', 'peer support'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Dashboard', url: '/dashboard' },
      { name: 'Connections & Stories', url: '/connections' }
    ]
  });

  useEffect(() => {
    loadCommunityData();
  }, []);

  const loadCommunityData = () => {
    // Mock data - in a real app, this would come from your API
    const mockStories: Story[] = [
      {
        id: '1',
        title: 'From Zero to Credit Hero: My 6-Month Journey',
        content: 'I had no credit history and was denied for everything. Here\'s how I built my credit score from 0 to 750 in just 6 months...',
        author: {
          name: 'Maria Rodriguez',
          avatar: '/api/placeholder/40/40',
          location: 'Austin, TX',
          joinDate: '2023-06-15',
          verified: true
        },
        category: 'banking',
        tags: ['credit', 'banking', 'success story', 'financial planning'],
        likes: 127,
        comments: 23,
        shares: 8,
        views: 1247,
        createdAt: '2024-01-20T10:30:00Z',
        updatedAt: '2024-01-20T10:30:00Z',
        isLiked: false,
        isBookmarked: true,
        type: 'success',
        helpful: 89,
        difficulty: 'medium',
        timeToRead: 5
      },
      {
        id: '2',
        title: 'Best Neighborhoods in Seattle for New Residents',
        content: 'After living in Seattle for 2 years, I\'ve compiled a list of the most welcoming neighborhoods with great public transportation, diverse communities, and affordable housing...',
        author: {
          name: 'Ahmed Hassan',
          avatar: '/api/placeholder/40/40',
          location: 'Seattle, WA',
          joinDate: '2022-03-10',
          verified: true
        },
        category: 'housing',
        tags: ['housing', 'seattle', 'neighborhoods', 'relocation'],
        likes: 89,
        comments: 15,
        shares: 12,
        views: 892,
        createdAt: '2024-01-19T14:20:00Z',
        updatedAt: '2024-01-19T14:20:00Z',
        isLiked: true,
        isBookmarked: false,
        type: 'advice',
        helpful: 67,
        difficulty: 'easy',
        timeToRead: 3
      },
      {
        id: '3',
        title: 'Tax Filing Help: What Documents Do I Need?',
        content: 'I\'m filing my taxes for the first time and I\'m overwhelmed by all the forms. Can someone help me understand what documents I need to gather?',
        author: {
          name: 'Priya Patel',
          avatar: '/api/placeholder/40/40',
          location: 'Chicago, IL',
          joinDate: '2023-11-05',
          verified: false
        },
        category: 'taxes',
        tags: ['taxes', 'help', 'documents', 'first time'],
        likes: 45,
        comments: 31,
        shares: 3,
        views: 567,
        createdAt: '2024-01-18T09:15:00Z',
        updatedAt: '2024-01-18T09:15:00Z',
        isLiked: false,
        isBookmarked: false,
        type: 'question',
        helpful: 23,
        difficulty: 'medium',
        timeToRead: 2
      }
    ];

    const mockMentors: Mentor[] = [
      {
        id: '1',
        name: 'Dr. Sarah Chen',
        title: 'Financial Advisor',
        company: 'Chase Bank',
        avatar: '/api/placeholder/60/60',
        location: 'San Francisco, CA',
        expertise: ['Banking', 'Credit Building', 'Investment'],
        experience: 8,
        rating: 4.9,
        reviews: 156,
        languages: ['English', 'Mandarin', 'Cantonese'],
        availability: 'available',
        responseTime: '< 2 hours',
        verified: true,
        bio: 'Certified financial advisor with 8 years of experience helping people build their financial foundation.',
        specializations: ['Credit Building', 'Banking Setup', 'Investment Planning'],
        achievements: ['CFP Certified', 'Top 1% Advisor', 'Financial Success Award'],
        isOnline: true,
        lastActive: '2024-01-20T15:30:00Z'
      },
      {
        id: '2',
        name: 'Carlos Mendez',
        title: 'Immigration Attorney',
        company: 'Mendez & Associates',
        avatar: '/api/placeholder/60/60',
        location: 'Miami, FL',
        expertise: ['Immigration Law', 'Visa Applications', 'Naturalization'],
        experience: 12,
        rating: 4.8,
        reviews: 203,
        languages: ['English', 'Spanish', 'Portuguese'],
        availability: 'available',
        responseTime: '< 4 hours',
        verified: true,
        bio: 'Immigration attorney specializing in helping Latin American immigrants navigate the US immigration system.',
        specializations: ['Visa Applications', 'Green Card Process', 'Naturalization'],
        achievements: ['Bar Certified', 'AILA Member', 'Pro Bono Award'],
        isOnline: false,
        lastActive: '2024-01-20T12:00:00Z'
      }
    ];

    const mockStats: CommunityStats = {
      totalMembers: 15420,
      activeToday: 1247,
      storiesShared: 3892,
      questionsAnswered: 15673,
      mentorsAvailable: 47,
      countriesRepresented: 89,
      languagesSupported: 23
    };

    setStories(mockStories);
    setMentors(mockMentors);
    setStats(mockStats);
  };

  const categories = [
    { id: 'all', name: 'All Topics', icon: Globe, color: 'bg-gray-500' },
    { id: 'banking', name: 'Banking & Credit', icon: CreditCard, color: 'bg-blue-500' },
    { id: 'housing', name: 'Housing & Living', icon: Home, color: 'bg-green-500' },
    { id: 'career', name: 'Career & Education', icon: GraduationCap, color: 'bg-purple-500' },
    { id: 'taxes', name: 'Taxes & Finance', icon: FileText, color: 'bg-orange-500' },
    { id: 'insurance', name: 'Insurance', icon: Shield, color: 'bg-red-500' },
    { id: 'community', name: 'Community & Support', icon: Users, color: 'bg-teal-500' }
  ];

  const storyTypes = [
    { id: 'all', name: 'All Types', icon: MessageSquare },
    { id: 'success', name: 'Success Stories', icon: Award },
    { id: 'advice', name: 'Advice & Tips', icon: Lightbulb },
    { id: 'question', name: 'Questions', icon: HelpCircle },
    { id: 'resource', name: 'Resources', icon: BookOpen },
    { id: 'celebration', name: 'Celebrations', icon: PartyPopper }
  ];

  const filteredStories = stories.filter(story => {
    const matchesCategory = selectedCategory === 'all' || story.category === selectedCategory;
    const matchesType = selectedType === 'all' || story.type === selectedType;
    const matchesSearch = searchTerm === '' || 
      story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesType && matchesSearch;
  });

  const filteredMentors = mentors.filter(mentor => {
    const matchesSearch = searchTerm === '' || 
      mentor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mentor.expertise.some(exp => exp.toLowerCase().includes(searchTerm.toLowerCase())) ||
      mentor.specializations.some(spec => spec.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'text-green-600 bg-green-100';
      case 'advice': return 'text-blue-600 bg-blue-100';
      case 'question': return 'text-orange-600 bg-orange-100';
      case 'resource': return 'text-purple-600 bg-purple-100';
      case 'celebration': return 'text-pink-600 bg-pink-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'busy': return 'text-yellow-600 bg-yellow-100';
      case 'away': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!stats) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          <Users className="w-10 h-10 inline-block mr-3 text-primary-600" />
          Connections & Stories
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Community space for peer stories, mentor Q&A, and mutual support. 
          Connect with others on similar journeys and learn from their experiences.
        </p>
      </div>

      {/* Community Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={stats.totalMembers} />
          </div>
          <p className="text-sm text-gray-600">Community Members</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Zap className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={stats.activeToday} />
          </div>
          <p className="text-sm text-gray-600">Active Today</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <MessageSquare className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={stats.storiesShared} />
          </div>
          <p className="text-sm text-gray-600">Stories Shared</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Award className="w-6 h-6 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={stats.mentorsAvailable} />
          </div>
          <p className="text-sm text-gray-600">Mentors Available</p>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('stories')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'stories' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Community Stories
          </button>
          <button
            onClick={() => setViewMode('mentors')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'mentors' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Find Mentors
          </button>
        </div>

        {viewMode === 'stories' && (
          <button
            onClick={() => setShowNewPost(true)}
            className="btn btn-primary flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Share Your Story
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={`Search ${viewMode === 'stories' ? 'stories' : 'mentors'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            {viewMode === 'stories' && (
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="success">Success Stories</option>
                <option value="advice">Advice & Tips</option>
                <option value="question">Questions</option>
                <option value="resource">Resources</option>
                <option value="celebration">Celebrations</option>
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center px-4 py-2 rounded-full border transition-all duration-200 ${
              selectedCategory === category.id
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            <category.icon className="w-4 h-4 mr-2" />
            {category.name}
          </button>
        ))}
      </div>

      {/* Content */}
      {viewMode === 'stories' ? (
        <div className="space-y-6">
          {filteredStories.map((story) => (
            <div
              key={story.id}
              className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              {/* Story Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <img
                      src={story.author.avatar}
                      alt={story.author.name}
                      className="w-12 h-12 rounded-full mr-4"
                    />
                    <div>
                      <div className="flex items-center">
                        <h3 className="font-semibold text-gray-900">{story.author.name}</h3>
                        {story.author.verified && (
                          <CheckCircle className="w-4 h-4 text-blue-500 ml-2" />
                        )}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-3 h-3 mr-1" />
                        {story.author.location}
                        <span className="mx-2">•</span>
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTimeAgo(story.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(story.type)}`}>
                      {story.type}
                    </span>
                    <button className="p-2 text-gray-400 hover:text-gray-600">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-3">{story.title}</h2>
                <p className="text-gray-700 mb-4 line-clamp-3">{story.content}</p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {story.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Story Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {story.timeToRead} min read
                    </div>
                    <div className="flex items-center">
                      <Eye className="w-4 h-4 mr-1" />
                      {story.views} views
                    </div>
                    <div className="flex items-center">
                      <ThumbsUp className="w-4 h-4 mr-1" />
                      {story.helpful} helpful
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(story.difficulty)}`}>
                      {story.difficulty}
                    </span>
                  </div>
                </div>
              </div>

              {/* Story Actions */}
              <div className="p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <button className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                      story.isLiked ? 'text-red-600' : 'text-gray-600 hover:text-red-600'
                    }`}>
                      <Heart className={`w-4 h-4 ${story.isLiked ? 'fill-current' : ''}`} />
                      {story.likes}
                    </button>
                    <button className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
                      <MessageCircle className="w-4 h-4" />
                      {story.comments}
                    </button>
                    <button className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-green-600 transition-colors">
                      <Share2 className="w-4 h-4" />
                      {story.shares}
                    </button>
                  </div>
                  <button className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    story.isBookmarked ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'
                  }`}>
                    <BookOpen className={`w-4 h-4 ${story.isBookmarked ? 'fill-current' : ''}`} />
                    {story.isBookmarked ? 'Saved' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMentors.map((mentor) => (
            <div
              key={mentor.id}
              className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              {/* Mentor Header */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="relative">
                      <img
                        src={mentor.avatar}
                        alt={mentor.name}
                        className="w-16 h-16 rounded-full"
                      />
                      {mentor.isOnline && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <h3 className="font-semibold text-gray-900">{mentor.name}</h3>
                        {mentor.verified && (
                          <CheckCircle className="w-4 h-4 text-blue-500 ml-2" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{mentor.title}</p>
                      <p className="text-sm text-gray-500">{mentor.company}</p>
                    </div>
                  </div>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getAvailabilityColor(mentor.availability)}`}>
                    {mentor.availability}
                  </span>
                </div>

                <p className="text-gray-700 mb-4 line-clamp-2">{mentor.bio}</p>

                {/* Expertise */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Expertise</h4>
                  <div className="flex flex-wrap gap-1">
                    {mentor.expertise.slice(0, 3).map((skill, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                    {mentor.expertise.length > 3 && (
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        +{mentor.expertise.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Star className="w-4 h-4 text-yellow-500 mr-1" />
                    {mentor.rating} ({mentor.reviews} reviews)
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {mentor.responseTime}
                  </div>
                </div>

                {/* Languages */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Languages</h4>
                  <div className="flex flex-wrap gap-1">
                    {mentor.languages.map((language, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Message
                  </button>
                  <button className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Connect
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {(viewMode === 'stories' && filteredStories.length === 0) || (viewMode === 'mentors' && filteredMentors.length === 0) ? (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No {viewMode} found</h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search criteria or explore different categories.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              setSelectedType('all');
            }}
            className="btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      ) : null}

      {/* Call to Action */}
      <div className="mt-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">Join Our Growing Community</h2>
        <p className="text-lg mb-6 opacity-90">
          Connect with thousands of immigrants who are building their American dream. 
          Share your story, ask questions, and find mentors who can guide you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => setShowNewPost(true)}
            className="btn bg-white text-primary-600 hover:bg-gray-100"
          >
            Share Your Story
          </button>
          <button
            onClick={() => setViewMode('mentors')}
            className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600"
          >
            Find a Mentor
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectionsPage;
